import { captureException } from '@sentry/react';
import { addMilliseconds, millisecondsToSeconds } from 'date-fns';
import { match, P } from 'ts-pattern';
import { Hex } from 'viem';
import { ORDER_STOP_TYPES } from '../constants';
import { SchemaSubmitDryOrderDto } from '../generated/apiTypes';
import { Order } from '../hooks/useOrders';
import { Position } from '../hooks/usePositions';
import { ProductType } from '../hooks/useProducts';
import { OrderSide } from '../types/generated';
import {
  CloseMarketOrder,
  RawOrderFormData,
  SimpleMarketOrder,
  TimeInForce,
  ValidatedOrder,
} from '../types/orders';
import { formatDollar } from './formatters';
import { Gwei } from './gwei';
import { stringifyBigInt } from './objectHelpers';
import { PrettyMerge } from './tsUtilityTypes';

export function getTradeLabel(side: Order['side'], reduceOnly: Order['reduceOnly']): string {
  if (side === OrderSide.BUY) {
    return reduceOnly ? 'Close Short' : 'Open Long';
  } else {
    return reduceOnly ? 'Close Long' : 'Open Short';
  }
}

export const getPositionFillsTradeLabel = ({
  positionOpenSide,
  side,
  isFirstFill,
  isLastFill,
}: {
  positionOpenSide: Order['side'];
  side: Order['side'];
  isFirstFill: boolean;
  isLastFill: boolean;
}) => {
  const sideDisplay = positionOpenSide === OrderSide.BUY ? 'Long' : 'Short';
  if (isFirstFill) {
    return `Open ${sideDisplay}`;
  }
  if (isLastFill) {
    return `Close ${sideDisplay}`;
  }
  if (side === positionOpenSide) {
    return `Increase ${sideDisplay}`;
  } else {
    return `Decrease ${sideDisplay}`;
  }
};

export const orderStatusLabels: Record<Order['status'], string> = {
  CANCELED: 'Cancelled',
  FILLED: 'Filled',
  FILLED_PARTIAL: 'Partially Filled',
  NEW: 'New',
  SUBMITTED: 'Submitted',
  REJECTED: 'Rejected',
  PENDING: 'Pending',
  EXPIRED: 'Expired',
};

export type CreateOrderDataDtoParams = PrettyMerge<
  {
    subaccountName: Hex;
    sender: Hex;
    product: ProductType;
    nonce: string;
    signedAt: number;
  },
  ValidatedOrder
>;

export const createFullMarketClose = (position: Position): CloseMarketOrder | SimpleMarketOrder => {
  const side = position.side === OrderSide.BUY ? OrderSide.SELL : OrderSide.BUY;

  return {
    discriminator: 'SimpleMarketOrder',
    type: 'MARKET',
    side,
    amount: Gwei.abs(position.size),
    reduceOnly: true,
  };
};

/**
 * This function transforms a ValidatedOrder and add product/subaccount data to create OrderDto data.
 * The transformation is removing the `discriminator` field and converts Gwei BigInts to strings.
 */
export const createOrderDataDto = ({
  subaccountName,
  sender,
  product,
  nonce,
  signedAt,
  amount,
  side,
  reduceOnly,
  ...orderParams
}: CreateOrderDataDtoParams) => {
  const baseData = {
    subaccount: subaccountName,
    sender,
    onchainId: product.onchainId,
    engineType: product.engineType,
    nonce,
    signedAt,
    side,
    reduceOnly,
    quantity: Gwei.fmt(amount),
  } as const;
  return match(orderParams)
    .returnType<SchemaSubmitDryOrderDto['data']>()
    .with(
      { discriminator: 'SimpleLimitOrder' },
      ({ discriminator: _, ...data }) =>
        ({
          ...baseData,
          ...data,
          price: Gwei.fmt(data.price),
        }) satisfies SchemaSubmitDryOrderDto['data']
    )
    .with(
      { discriminator: 'OtoLimitOrder' },
      ({ discriminator: _, stopLoss: _1, takeProfit: _2, ...data }) =>
        ({
          ...baseData,
          ...data,
          price: Gwei.fmt(data.price),
        }) satisfies SchemaSubmitDryOrderDto['data']
    )

    .with(
      { discriminator: 'SimpleMarketOrder' },
      () =>
        ({
          ...baseData,
          type: 'MARKET',
        }) satisfies SchemaSubmitDryOrderDto['data']
    )
    .with(
      { discriminator: 'CloseMarketOrder' },
      ({ discriminator: _, ...data }) =>
        ({
          ...baseData,
          ...data,
          quantity: '0',
        }) satisfies SchemaSubmitDryOrderDto['data']
    )
    .with(
      { discriminator: 'PositionStopOrder' },
      ({ discriminator: _, ...data }) =>
        ({
          ...baseData,
          ...data,
          quantity: '0',
          stopPrice: Gwei.fmt(data.stopPrice),
        }) satisfies SchemaSubmitDryOrderDto['data']
    )
    .with(
      { discriminator: 'OtocoStopOrder' },
      ({ discriminator: _, ...data }) =>
        ({
          ...baseData,
          ...data,
          stopPrice: Gwei.fmt(data.stopPrice),
        }) satisfies SchemaSubmitDryOrderDto['data']
    )
    .exhaustive();
};

const calculateExpiresAt = (timeInForce?: TimeInForce, expiresFromNow?: number) => {
  return (!timeInForce || timeInForce === 'GTD') && expiresFromNow !== undefined
    ? millisecondsToSeconds(addMilliseconds(new Date(), expiresFromNow).getTime())
    : undefined;
};

/**
 * This function validates that orderForm data is constructed correctly to be used to convert to Order DTO.
 * The idea is that if this throws there's a developer error.
 * It validates that all types and combination of fields are correct. This still doesn't guarantee that the order will go through,
 * to be certain the order will go through `useDryRunOrder` should be used.
 */
export const validateOrderFormData = (rawParams: RawOrderFormData) => {
  const error = (message: string) => () => {
    const error = new Error(`${message} \n ${stringifyBigInt(params)}`);
    console.error(error);
    captureException(error);
    return undefined;
  };
  const { expiresFromNow, ...params } = rawParams;
  return (
    match(params)
      .returnType<ValidatedOrder | undefined>()
      // Validations
      .with(
        { type: 'MARKET', price: P.bigint },
        error('Invalid order data: Market orders should not have a limit price.')
      )
      .with(
        { type: 'LIMIT', price: P.optional(undefined) },
        error('Invalid order data: Limit orders require a limit price.')
      )
      .with(
        P.union(
          { stopPrice: P.bigint, stopType: P.optional(undefined) },
          { stopPrice: P.optional(undefined), stopType: P.number }
        ),
        error('Invalid order data: Stop order needs both stopType and stopPrice.')
      )

      // Valid unsupported cases
      // TODO this should be removed when stop limit order support is added
      .with(
        P.union({ type: 'LIMIT', stopPrice: P.bigint }, { type: 'LIMIT', stopType: P.number }),
        error('Invalid order data: Limit stop orders not supported yet.')
      )

      // Valid cases
      .with(
        P.union({
          type: 'MARKET',
          stopPrice: P.bigint,
          stopType: P.number,
          otocoGroupId: P.string,
          close: P.union(false, undefined),
        }),
        (data) => ({
          ...data,
          discriminator: 'OtocoStopOrder',
          reduceOnly: true,
          close: false,
        })
      )
      .with(
        {
          type: 'MARKET',
          close: true,
          stopType: P.union(ORDER_STOP_TYPES.LOSS, ORDER_STOP_TYPES.GAIN),
          stopPrice: P.bigint,
        },
        (data) =>
          ({
            ...data,
            discriminator: 'PositionStopOrder',
            reduceOnly: true,
            amount: 0n,
          }) satisfies ValidatedOrder
      )
      .with(
        P.union(
          { type: 'LIMIT', close: false, price: P.bigint, stopLoss: P.bigint },
          { type: 'LIMIT', close: false, price: P.bigint, takeProfit: P.bigint }
        ),
        (data) => {
          return {
            ...data,
            discriminator: 'OtoLimitOrder',
            timeInForce: data.timeInForce ?? 'GTD',
            expiresAt: calculateExpiresAt(params.timeInForce, expiresFromNow),
            postOnly: data.postOnly ?? false,
            reduceOnly: data.reduceOnly ?? false,
            otocoTrigger: true,
          } satisfies ValidatedOrder;
        }
      )
      .with(
        { type: 'MARKET', close: true },
        (data) =>
          ({
            ...data,
            discriminator: 'CloseMarketOrder',
            reduceOnly: true,
            amount: 0n,
          }) satisfies ValidatedOrder
      )
      .with(
        { type: 'LIMIT', close: false, price: P.bigint },
        (data) =>
          ({
            ...data,
            discriminator: 'SimpleLimitOrder',
            timeInForce: data.timeInForce ?? 'GTD',
            expiresAt: calculateExpiresAt(params.timeInForce, expiresFromNow),
            postOnly: data.postOnly ?? false,
            reduceOnly: data.reduceOnly ?? false,
          }) satisfies ValidatedOrder
      )

      .with(
        { type: 'MARKET', close: false },
        (data) =>
          ({
            ...data,
            discriminator: 'SimpleMarketOrder',
            reduceOnly: data.reduceOnly ?? false,
          }) satisfies ValidatedOrder
      )
      .otherwise(error(`Invalid order data`))
  );
};

/**
 * Selects the first relevant stop loss and take profit orders for each product.
 *
 * When multiple stop orders exist for the same product, we select the one that would
 * trigger first based on price movement:
 *
 * - Stop Loss:
 *   - For BUY side (covering short position): the lowest price (triggered first as price rises)
 *   - For SELL side (covering long position): the highest price (triggered first as price falls)
 * - Take Profit:
 *   - For BUY side (covering short position): the highest price (triggered first as price falls)
 *   - For SELL side (covering long position): the lowest price (triggered first as price rises)
 *
 * @param orders Array of pending stop orders with close=true
 * @returns Record of productIds with their first stop loss and take profit orders
 */
export function selectFirstStopOrders(
  orders: Order[]
): Record<string, { stopLoss: Order | undefined; takeProfit: Order | undefined }> {
  // Handle empty orders array
  if (!orders || orders.length === 0) {
    return {};
  }

  // Group orders by product ID
  const ordersByProductId = orders.reduce<Record<string, Order[]>>((acc, order) => {
    if (!acc[order.productId]) {
      acc[order.productId] = [];
    }
    acc[order.productId]?.push(order);
    return acc;
  }, {});

  // Process each product's orders to find first SL and TP
  const filtered = Object.entries(ordersByProductId).map(([productId, productOrders]) => {
    // Find the stop loss that would trigger first
    const stopLossOrders = productOrders
      .filter((o) => o.stopType === ORDER_STOP_TYPES.LOSS)
      .sort((a, b) => {
        if (a.side === OrderSide.BUY) {
          return Number(a.price - b.price); // Sort ascending for BUY (lowest price first)
        }
        return Number(b.price - a.price); // Sort descending for SELL (highest price first)
      });

    // Find the take profit that would trigger first
    const takeProfitOrders = productOrders
      .filter((o) => o.stopType === ORDER_STOP_TYPES.GAIN)
      .sort((a, b) => {
        if (a.side === OrderSide.BUY) {
          return Number(b.price - a.price); // Sort descending for BUY (highest price first)
        }
        return Number(a.price - b.price); // Sort ascending for SELL (lowest price first)
      });

    return {
      productId,
      stopLoss: stopLossOrders[0], // undefined if no stop loss
      takeProfit: takeProfitOrders[0], // undefined if no take profit
    };
  });

  // Convert array to record for easier lookup
  return filtered.reduce<
    Record<string, { stopLoss: Order | undefined; takeProfit: Order | undefined }>
  >((acc, curr) => {
    acc[curr.productId] = {
      stopLoss: curr.stopLoss,
      takeProfit: curr.takeProfit,
    };
    return acc;
  }, {});
}

export const getTriggerCondition = (
  { stopType, stopPrice }: Pick<Order, 'stopType' | 'stopPrice'>,
  priceDecimals: number
) => {
  return match({ stopType, stopPrice })
    .with(
      { stopType: ORDER_STOP_TYPES.GAIN, stopPrice: P.bigint },
      ({ stopPrice }) =>
        `Price ≥ ${formatDollar(stopPrice, { minimumFractionDigits: priceDecimals, maximumFractionDigits: priceDecimals })}`
    )
    .with(
      { stopType: ORDER_STOP_TYPES.LOSS, stopPrice: P.bigint },
      ({ stopPrice }) =>
        `Price ≤ ${formatDollar(stopPrice, { minimumFractionDigits: priceDecimals, maximumFractionDigits: priceDecimals })}`
    )
    .otherwise(() => '-');
};

export function sortOrdersByStopType(orders: Order[]): Order[] {
  const getSortValue = (stopType: Order['stopType']) => {
    if (stopType === 0) {
      return 0;
    }
    if (stopType === 1) {
      return 1;
    }
    return 2;
  };

  return [...orders].sort((a, b) => {
    const valueA = getSortValue(a.stopType);
    const valueB = getSortValue(b.stopType);
    return valueA - valueB;
  });
}
